import React, { useCallback, useMemo, useRef } from 'react';
import './TabGroups.css';

/**
 * An app's sections, grouped by what KIND of thing you are touching.
 *
 * Nine tabs stood in one undifferentiated strip, and three unrelated jobs were
 * mixed into it: four of them stage edits into a draft, three hand you
 * credentials, and the rest are measurements that cannot be edited at all.
 * Nothing in the strip said which was which — so the most consequential fact
 * about a tab, whether pressing Save changes what users see, was something you
 * learned by pressing Save.
 *
 * The group says it instead, once, for all the tabs underneath it. That is the
 * whole idea: the grouping is not decoration, it is the answer to "what happens
 * if I change something here".
 *
 * NOTHING WAS REMOVED. Every panel that existed still exists; Testing split into
 * the two separate jobs it was already stacking vertically, and Earnings gained a
 * home beside the app's other measurements instead of living only under Money.
 */

/**
 * The staged dot rides on a tab whose field is in the draft. It is deliberately
 * a mark rather than a count: the number belongs to the group (where committing
 * happens), and repeating it per tab would suggest four separate commits.
 */
function Dot() {
  return <i className="tabgroups__dot" aria-hidden="true" />;
}

function TabGroups({ groups, active, onSelect, disabled = false, ariaLabel = 'App sections' }) {
  const cellRefs = useRef(new Map());

  // Flattened once, so the arrow-key walk crosses group boundaries the way the
  // eye does. A keyboard user should not have to know where a group ends.
  const flat = useMemo(
    () => groups.flatMap(g => (g.tabs || []).map(t => ({ ...t, groupId: g.id }))),
    [groups],
  );

  const activeIndex = Math.max(0, flat.findIndex(t => t.id === active));

  const moveTo = useCallback((i) => {
    const t = flat[i];
    if (!t) return;
    cellRefs.current.get(t.id)?.focus();
    if (!disabled) onSelect(t.id);
  }, [flat, disabled, onSelect]);

  // The strip is ONE tab stop: the standing tab holds the tabindex, arrows walk
  // and wrap, Home/End jump. Selection follows focus, which is the behaviour for
  // a tablist whose panels are already loaded.
  const onKeyDown = useCallback((e, i) => {
    const last = flat.length - 1;
    if (e.key === 'ArrowRight') { e.preventDefault(); moveTo(i === last ? 0 : i + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); moveTo(i === 0 ? last : i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); moveTo(0); }
    else if (e.key === 'End') { e.preventDefault(); moveTo(last); }
  }, [flat.length, moveTo]);

  let cursor = -1;

  return (
    <div className="tabgroups" role="tablist" aria-label={ariaLabel}>
      {groups.map(group => (
        // role="presentation" keeps the tabs the tablist's own children while
        // still letting the group draw its label and seam. A <div role="group">
        // here would put a non-tab between the list and its tabs.
        <div className="tabgroups__group" key={group.id} role="presentation">
          <div className="tabgroups__head">
            <span className="tabgroups__name">{group.label}</span>
            {group.badge && <span className="tabgroups__badge">{group.badge}</span>}
            {group.why && <span className="tabgroups__why">{group.why}</span>}
          </div>
          <div className="tabgroups__tabs" role="presentation">
            {(group.tabs || []).map(tab => {
              cursor += 1;
              const i = cursor;
              const isActive = tab.id === active;
              return (
                <button
                  key={tab.id}
                  ref={(el) => {
                    if (el) cellRefs.current.set(tab.id, el);
                    else cellRefs.current.delete(tab.id);
                  }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className="tabgroups__tab"
                  data-active={isActive || undefined}
                  disabled={disabled && !isActive}
                  tabIndex={i === activeIndex ? 0 : -1}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  onClick={() => { if (!disabled) onSelect(tab.id); }}
                >
                  {tab.label}
                  {tab.staged && <Dot />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default TabGroups;
